import { NestedTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { ContextMenuItem } from '@models/editorState/contextMenuItem.model';
import { TreeNode } from '@models/editorState/treeNode.model';
import { EscritoDto } from '@models/escrito/escritoDto.model';
import { PlantillaEscritoDto } from '@models/plantillaEscrito/planillaEscritoDto.model';
import { EditorStateService } from '@services/editorState/editor-state.service';
import { EscritoService } from '@services/escrito/escrito.service';
import { PlantillaEscritoService } from '@services/plantillaEscrito/plantillaEscrito.service';
import { TipoEscritoService } from '@services/tipoEscrito/tipoEscrito.service';
import { ConfirmDialogComponent } from '@views/escrito-editor/dialogs/confirm-dialog/confirm-dialog/confirm-dialog.component';
import { FiltrosDialogComponent } from '@views/escrito-editor/dialogs/filtros-dialog/filtros-dialog/filtros-dialog.component';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';

@Component({
  selector: 'app-explorador-panel',
  standalone: true,
  imports: [CommonModule,MatIconModule,MatButtonModule,MatTreeModule,MatFormFieldModule,ReactiveFormsModule,MatInputModule,MatTooltipModule,MatProgressSpinnerModule,MatMenuModule,MatDialogModule,MatSnackBarModule],
  templateUrl: './explorador-panel.component.html',
  styleUrls: ['./explorador-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExploradorPanelComponent implements OnInit, OnDestroy {
  searchControl = new FormControl('');
  treeControl = new NestedTreeControl<TreeNode>(node => node.children);
  dataSource = new MatTreeNestedDataSource<TreeNode>();

  showEscritos = true;
  showPlantillas = true;
  isLoading = false;

  contextMenuPosition = { x: '0px', y: '0px' };
  contextMenuNode: TreeNode | null = null;
  showContextMenu = false;

  private destroy$ = new Subject<void>();

  constructor(
    private escritoService: EscritoService,
    private plantillaService: PlantillaEscritoService,
    private editorStateService: EditorStateService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private tipoEscritoService: TipoEscritoService
  ) {}

  ngOnInit(): void {
    this.setupSearch();
    this.loadTreeData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        this.filterTree(searchTerm || '');
      });
  }

  private loadTreeData(): void {
    this.isLoading = true;

    Promise.all([
      this.showEscritos ? this.escritoService.getAll().toPromise() : Promise.resolve([]),
      this.showPlantillas ? this.plantillaService.getAll().toPromise() : Promise.resolve([]),
      this.tipoEscritoService.getAll().toPromise()
    ]).then(([escritos, plantillas, tipos]) => {
      const treeData = this.buildTreeData(escritos || [], plantillas || [], tipos || []);
      this.dataSource.data = treeData;
      this.isLoading = false;

      // Expandir los primeros niveles
      this.treeControl.dataNodes.forEach(node => {
        if (node.type === 'folder') {
          this.treeControl.expand(node);
        }
      });
    }).catch(error => {
      console.error('Error cargando datos del explorador:', error);
      this.isLoading = false;
      this.snackBar.open('Error cargando datos', 'Cerrar', { duration: 3000 });
    });
  }

  private buildTreeData(escritos: EscritoDto[], plantillas: PlantillaEscritoDto[], tipos: any[]): TreeNode[] {
    const tree: TreeNode[] = [];

    if (this.showEscritos && escritos.length > 0) {
      // Agrupar escritos por tipo
      const escritosPorTipo = this.groupByTipo(escritos, tipos);

      tree.push({
        id: 'escritos-root',
        name: `Escritos (${escritos.length})`,
        type: 'folder',
        icon: 'edit_note',
        expanded: true,
        children: escritosPorTipo
      });
    }

    if (this.showPlantillas && plantillas.length > 0) {
      // Agrupar plantillas por tipo
      const plantillasPorTipo = this.groupPlantillasByTipo(plantillas, tipos);

      tree.push({
        id: 'plantillas-root',
        name: `Plantillas (${plantillas.length})`,
        type: 'folder',
        icon: 'description',
        expanded: true,
        children: plantillasPorTipo
      });
    }

    return tree;
  }

  private groupByTipo(escritos: EscritoDto[], tipos: any[]): TreeNode[] {
    const grupos: { [key: string]: EscritoDto[] } = {};

    escritos.forEach(escrito => {
      const tipoId = escrito.tipoEscrito?.id || 0;
      const tipoNombre = escrito.tipoEscrito?.descripcion || 'Sin Tipo';
      const key = `${tipoId}_${tipoNombre}`;

      if (!grupos[key]) {
        grupos[key] = [];
      }
      grupos[key].push(escrito);
    });

    return Object.keys(grupos).map(key => {
      const [, tipoNombre] = key.split('_');
      const escritosDelTipo = grupos[key];

      return {
        id: `tipo-escrito-${key}`,
        name: `${tipoNombre} (${escritosDelTipo.length})`,
        type: 'folder',
        icon: 'folder',
        expanded: false,
        children: escritosDelTipo.map(escrito => ({
          id: `escrito-${escrito.id}`,
          name: escrito.titulo || `Escrito ${escrito.id}`,
          type: 'escrito',
          icon: 'edit_note',
          entity: escrito
        } as TreeNode))
      };
    });
  }

  private groupPlantillasByTipo(plantillas: PlantillaEscritoDto[], tipos: any[]): TreeNode[] {
    const grupos: { [key: string]: PlantillaEscritoDto[] } = {};

    plantillas.forEach(plantilla => {
      const tipoId = plantilla.tipoEscrito?.id || 0;
      const tipoNombre = plantilla.tipoEscrito?.descripcion || 'Sin Tipo';
      const key = `${tipoId}_${tipoNombre}`;

      if (!grupos[key]) {
        grupos[key] = [];
      }
      grupos[key].push(plantilla);
    });

    return Object.keys(grupos).map(key => {
      const [, tipoNombre] = key.split('_');
      const plantillasDelTipo = grupos[key];

      return {
        id: `tipo-plantilla-${key}`,
        name: `${tipoNombre} (${plantillasDelTipo.length})`,
        type: 'folder',
        icon: 'folder',
        expanded: false,
        children: plantillasDelTipo.map(plantilla => ({
          id: `plantilla-${plantilla.id}`,
          name: plantilla.titulo || `Plantilla ${plantilla.id}`,
          type: 'plantilla',
          icon: 'description',
          entity: plantilla
        } as TreeNode))
      };
    });
  }

  private filterTree(searchTerm: string): void {
    if (!searchTerm.trim()) {
      this.loadTreeData();
      return;
    }

    const filteredData = this.dataSource.data
      .map(root => this.filterNode(root, searchTerm.toLowerCase()))
      .filter(node => node !== null) as TreeNode[];

    this.dataSource.data = filteredData;

    // Expandir nodos con resultados
    this.treeControl.dataNodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        this.treeControl.expand(node);
      }
    });
  }

  private filterNode(node: TreeNode, searchTerm: string): TreeNode | null {
    if (node.name.toLowerCase().includes(searchTerm)) {
      return { ...node };
    }

    if (node.children) {
      const filteredChildren = node.children
        .map(child => this.filterNode(child, searchTerm))
        .filter(child => child !== null) as TreeNode[];

      if (filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren
        };
      }
    }

    return null;
  }

  hasChild = (_: number, node: TreeNode) => !!node.children && node.children.length > 0;

  onNodeClick(node: TreeNode): void {
    if (node.type === 'escrito' || node.type === 'plantilla') {
      this.openInEditor(node);
    } else if (node.type === 'folder') {
      this.treeControl.toggle(node);
    }
  }

  onNodeRightClick(event: MouseEvent, node: TreeNode): void {
    event.preventDefault();
    event.stopPropagation();

    this.contextMenuPosition.x = event.clientX + 'px';
    this.contextMenuPosition.y = event.clientY + 'px';
    this.contextMenuNode = node;
    this.showContextMenu = true;
  }

  onContextMenuAction(action: string): void {
    if (!this.contextMenuNode) return;

    switch (action) {
      case 'open':
        this.openInEditor(this.contextMenuNode);
        break;
      case 'duplicate':
        this.duplicateNode(this.contextMenuNode);
        break;
      case 'delete':
        this.deleteNode(this.contextMenuNode);
        break;
      case 'rename':
        this.renameNode(this.contextMenuNode);
        break;
    }

    this.closeContextMenu();
  }

  closeContextMenu(): void {
    this.showContextMenu = false;
    this.contextMenuNode = null;
  }

  private openInEditor(node: TreeNode): void {
    if (!node.entity) return;

    this.editorStateService.openTab({
      tipo: node.type as 'escrito' | 'plantilla',
      entityId: node.entity.id,
      titulo: node.name,
      contenido: node.entity.cuerpoHTML || '',
      entity: node.entity
    });
  }

  private duplicateNode(node: TreeNode): void {
    if (node.type === 'escrito' && node.entity) {
      this.escritoService.clone(node.entity.id).subscribe({
        next: () => {
          this.snackBar.open('Escrito duplicado exitosamente', 'Cerrar', { duration: 3000 });
          this.loadTreeData();
        },
        error: (error) => {
          console.error('Error duplicando escrito:', error);
          this.snackBar.open('Error al duplicar el escrito', 'Cerrar', { duration: 3000 });
        }
      });
    } else if (node.type === 'plantilla' && node.entity) {
      this.plantillaService.clone(node.entity.id).subscribe({
        next: () => {
          this.snackBar.open('Plantilla duplicada exitosamente', 'Cerrar', { duration: 3000 });
          this.loadTreeData();
        },
        error: (error) => {
          console.error('Error duplicando plantilla:', error);
          this.snackBar.open('Error al duplicar la plantilla', 'Cerrar', { duration: 3000 });
        }
      });
    }
  }

  private deleteNode(node: TreeNode): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: `Eliminar ${node.type}`,
        message: `¿Estás seguro de que deseas eliminar "${node.name}"?`,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && node.entity) {
        if (node.type === 'escrito') {
          this.escritoService.deleteLogic(node.entity.id).subscribe({
            next: () => {
              this.snackBar.open('Escrito eliminado exitosamente', 'Cerrar', { duration: 3000 });
              this.loadTreeData();
            },
            error: (error) => {
              console.error('Error eliminando escrito:', error);
              this.snackBar.open('Error al eliminar el escrito', 'Cerrar', { duration: 3000 });
            }
          });
        } else if (node.type === 'plantilla') {
          this.plantillaService.delete(node.entity.id).subscribe({
            next: () => {
              this.snackBar.open('Plantilla eliminada exitosamente', 'Cerrar', { duration: 3000 });
              this.loadTreeData();
            },
            error: (error) => {
              console.error('Error eliminando plantilla:', error);
              this.snackBar.open('Error al eliminar la plantilla', 'Cerrar', { duration: 3000 });
            }
          });
        }
      }
    });
  }

  private renameNode(node: TreeNode): void {
    // Implementar diálogo de renombrado
    console.log('Renombrar:', node.name);
  }

  onFilterClick(): void {
    const dialogRef = this.dialog.open(FiltrosDialogComponent, {
      width: '500px',
      data: {
        showEscritos: this.showEscritos,
        showPlantillas: this.showPlantillas
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.showEscritos = result.showEscritos;
        this.showPlantillas = result.showPlantillas;
        this.loadTreeData();
      }
    });
  }

  onRefresh(): void {
    this.loadTreeData();
  }

  onNewEscrito(): void {
    this.editorStateService.openTab({
      tipo: 'escrito',
      titulo: 'Nuevo Escrito',
      contenido: ''
    });
  }

  onNewPlantilla(): void {
    this.editorStateService.openTab({
      tipo: 'plantilla',
      titulo: 'Nueva Plantilla',
      contenido: ''
    });
  }

  getContextMenuItems(node: TreeNode): ContextMenuItem[] {
    const items: ContextMenuItem[] = [];

    if (node.type === 'escrito' || node.type === 'plantilla') {
      items.push(
        { label: 'Abrir', icon: 'open_in_new', action: 'open' },
        { label: 'Duplicar', icon: 'content_copy', action: 'duplicate' },
        { label: '', icon: '', action: '', divider: true },
        { label: 'Eliminar', icon: 'delete', action: 'delete' }
      );
    }

    return items;
  }


  // ============ GETTERS PARA TEMPLATE ============
  get hasSearchErrors(): boolean {
    return this.searchControl.invalid && this.searchControl.touched;
  }

  get searchErrorMessage(): string {
    if (this.searchControl.hasError('script')) {
      return 'No se permiten scripts en la búsqueda';
    }
    if (this.searchControl.hasError('sql')) {
      return 'No se permiten comandos SQL en la búsqueda';
    }
    if (this.searchControl.hasError('maxlength')) {
      return 'Máximo 100 caracteres';
    }
    return '';
  }
}
